import { Component, computed, inject } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { cloneDeep } from 'lodash';
import { ComponentsModule } from 'src/app/components/components.module';
import { ModalService } from 'src/app/components/modals/modal.service';
import { CapAlertsPageService } from '../../../services/cap-alerts/cap-alerts-page.service';
import { CapAlertsService } from '../../../services/integrations/component-integrations/cap-alerts/cap-alerts.service';
import {
  CapAlertFilters,
  CapAlertSettingsTabValue,
} from '../../../types/cap-alerts-page.types';
import { CapAlertFiltersDialogComponent } from '../cap-alert-filters-dialog/cap-alert-filters-dialog.component';

@Component({
  selector: 'cap-alerts-settings-tab',
  templateUrl: './cap-alerts-settings-tab.component.html',
  styleUrl: './cap-alerts-settings-tab.component.scss',
  imports: [
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    ComponentsModule,
  ],
})
export class CapAlertsSettingsTabComponent {
  protected readonly pageService = inject(CapAlertsPageService);
  private readonly modalService = inject(ModalService);
  private readonly capAlertsService = inject(CapAlertsService);

  protected readonly textFieldOptions = this.capAlertsService.textFieldsValues;
  protected readonly settings = computed(
    () => this.pageService.formValue().settings,
  );

  protected patch(partial: Partial<CapAlertSettingsTabValue>): void {
    const control = this.pageService.form.controls.settings;
    control.setValue({ ...control.value, ...partial });
  }

  protected isTextFieldChecked(key: string): boolean {
    return !!this.settings().textFields?.includes(key);
  }

  protected setTextField(key: string, checked: boolean): void {
    const current = this.settings().textFields ?? [];
    this.patch({
      textFields: checked
        ? current.includes(key)
          ? current
          : [...current, key]
        : current.filter((item) => item !== key),
    });
  }

  /** Alert duration is a count of minutes: no sign, no exponent. */
  protected onDefaultExpiryKeydown(event: KeyboardEvent): void {
    if (['-', '+', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
    }
  }

  protected onDefaultExpiryInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);

    if (value < 0 || isNaN(value)) {
      input.value = '0';
      this.patch({ defaultExpiry: 0 });
    } else {
      this.patch({ defaultExpiry: value });
    }
  }

  /** Cancelling the dialog leaves the filters as they were. */
  protected async editFilters(): Promise<void> {
    const control = this.pageService.form.controls.filters;

    try {
      const filters: CapAlertFilters = await this.modalService.showLargeDialog(
        CapAlertFiltersDialogComponent,
        { filters: cloneDeep(control.value) },
        { maxHeight: '100dvh' },
        'editFiltersButton',
      );
      control.setValue(filters);
    } catch {
      // Dismissed: nothing changes.
    }
  }
}
